import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * Set the category on a proposed family, across every row proposing it.
 *
 * Keyed by name rather than an id because proposals live on the rows, and
 * several rows routinely propose the same family — the backend folds them the
 * same way at commit. A family cannot be created without a category, so this is
 * the difference between a new-family row committing and failing.
 */
type TData = { data: { updated: number } };
type TError = AxiosError;
type TVariables = { runId: number; kind: 'family'; key: string; catalogCategoryId: number };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdatePriceImportProposalApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ runId, ...body }: TVariables) => {
    try {
      const { data } = await http.put(`${priceImportsEndpointIdentifier}/${runId}/proposals`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${priceImportsEndpointIdentifier}/proposals`],
    ...options,
  });
}
